import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { storage } from "@/lib/storage";

/**
 * Bright / dark theme. The script in index.html applies the theme before first paint; this keeps
 * it in sync, remembers the visitor's choice, and follows the system setting until they choose.
 */
export type Theme = "light" | "dark";
const KEY = "ma.theme";

type ThemeContextValue = { theme: Theme; setTheme: (t: Theme) => void; toggle: () => void };
const ThemeContext = createContext<ThemeContextValue>({ theme: "dark", setTheme: () => {}, toggle: () => {} });

const systemTheme = (): Theme => (window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark");
const saved = (): Theme | null => {
  const t = storage.get(KEY);
  return t === "light" || t === "dark" ? t : null;
};

function apply(theme: Theme, animate: boolean) {
  const html = document.documentElement;
  if (animate) {
    html.classList.add("theme-switching");
    window.setTimeout(() => html.classList.remove("theme-switching"), 480);
  }
  html.setAttribute("data-theme", theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "light" ? "#F3F6F0" : "#020C07");
  window.dispatchEvent(new CustomEvent("ma:themechange", { detail: theme }));
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => (document.documentElement.getAttribute("data-theme") as Theme | null) ?? saved() ?? systemTheme());

  useEffect(() => {
    apply(theme, false);
    // Follow the system setting while the visitor hasn't made a choice.
    const mql = window.matchMedia?.("(prefers-color-scheme: light)");
    if (!mql) return;
    const onChange = () => {
      if (saved()) return;
      const next = mql.matches ? "light" : "dark";
      setThemeState(next);
      apply(next, true);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTheme = useCallback((t: Theme) => {
    storage.set(KEY, t);
    setThemeState(t);
    apply(t, true);
  }, []);
  const toggle = useCallback(() => setTheme(theme === "light" ? "dark" : "light"), [setTheme, theme]);
  const value = useMemo(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
